import React from 'react';
import './BBCQuickListBrandHero.css';

/**
 * BBCQuickListBrandHero
 * ---------------------
 * Reusable brand block for the BBC Quick List system.
 *
 * - Pure HTML/CSS — no images, no canvas, no third-party deps.
 * - Dark navy + gold/orange premium look, brushed-metal shimmer animation.
 * - Three stacked gold "B / B / C" tiles on the left, headline stack on the right.
 * - Respects `prefers-reduced-motion`: animations stop, layout intact.
 * - Mobile-responsive: tiles drop above the text under ~640px.
 *
 * Props are optional so you can reuse this on the main site, member dashboard,
 * founder page, training pages, etc. without rebuilding it.
 */
export default function BBCQuickListBrandHero({
  primary = 'Quick List',
  secondary = 'Waiting List',
  tagline = 'Infrastructure Beats Hype',
  subline = 'Build Your List Before Launch',
  badges = ['Early Access Open', 'Builder List Active', 'Phase 1 Pre-Launch'],
  className = '',
  dataTestId = 'bbc-quick-list-brand-hero',
}) {
  return (
    <div className={`bbcql-hero ${className}`} data-testid={dataTestId}>
      <div className="bbcql-hero__frame" aria-hidden="true" />
      <div className="bbcql-hero__inner">
        {/* Left: stacked BBC blocks */}
        <div
          className="bbcql-hero__blocks"
          role="img"
          aria-label="BBC Quick List logomark"
        >
          <span className="bbcql-hero__sweep-bg" aria-hidden="true" />
          {['B', 'B', 'C'].map((letter, idx) => (
            <span
              key={`bbcql-tile-${letter}-${idx}`}
              className="bbcql-hero__tile"
              style={{ animationDelay: `${idx * 0.45}s` }}
              data-testid={`bbcql-hero-tile-${idx}`}
            >
              <span className="bbcql-hero__tile-letter">{letter}</span>
            </span>
          ))}
        </div>

        {/* Right: headline stack */}
        <div className="bbcql-hero__text">
          <h2 className="bbcql-hero__primary" data-testid="bbcql-hero-primary">
            <span className="bbcql-hero__primary-shimmer">{primary}</span>
          </h2>
          {secondary && (
            <p className="bbcql-hero__secondary" data-testid="bbcql-hero-secondary">
              {secondary}
            </p>
          )}
          {tagline && (
            <p className="bbcql-hero__tagline" data-testid="bbcql-hero-tagline">
              {tagline}
            </p>
          )}
          {subline && (
            <p className="bbcql-hero__subline" data-testid="bbcql-hero-subline">
              {subline}
            </p>
          )}
          {Array.isArray(badges) && badges.length > 0 && (
            <ul
              className="bbcql-hero__badges"
              role="list"
              data-testid="bbcql-hero-badges"
            >
              {badges.map((label, idx) => (
                <li
                  key={`bbcql-badge-${idx}-${label}`}
                  className="bbcql-hero__badge"
                  data-testid={`bbcql-hero-badge-${idx}`}
                >
                  <span className="bbcql-hero__badge-dot" aria-hidden="true" />
                  {label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
